'use client';

import { useState } from 'react';
import { UserDetails } from '@/utils/helpers';
import { CommentForm } from './CommentForm';
import { CommentContent } from './CommentContent';
import { CommentActions } from './CommentActions';
import { CommentWithUser } from './types';
import { CommentHeader } from './CommentHeader';
import ProfileAvatar from '../ProfileUser/ProfileAvatar';
import { OAuthLink } from '@/contexts/OAuthFlowContext';
import type { SupabaseClient } from '@supabase/supabase-js';

interface ReplyCommentItemProps {
  supabase: SupabaseClient;
  comment: CommentWithUser;
  userDetails: UserDetails | null;
  toneId: number;
  toneUsername: string;
  onReload?: (id: number) => void;
  onNestedReply?: ({ id, username }: { id: number; username: string }) => void;
  isApiSelect?: boolean;
}

export function ReplyCommentItem({
  comment,
  userDetails,
  toneId,
  toneUsername,
  onReload,
  onNestedReply,
  isApiSelect = false,
}: ReplyCommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);

  const isOwner = userDetails?.id === comment.user_id;
  const canReply = !comment.is_deleted;

  const handleOnReload = async () => {
    onReload?.(comment.id!);
  };

  const handleEditSubmit = async (id: number) => {
    setIsEditing(false);
    handleOnReload();
  };

  return (
    <div className="flex items-start gap-3">
      <OAuthLink
        href={isApiSelect ? `/api/v1/select/${comment.username}` : `/${comment.username}`}
        className="flex items-center gap-2"
      >
        <ProfileAvatar
          username={comment.username || ''}
          avatarUrl={comment.avatar_url || ''}
          ownProfile={false}
          size={32}
        />
      </OAuthLink>
      <div className="flex flex-col gap-3 w-full">
        <div className="flex flex-col gap-3">
          {/* Comment Header */}
          <CommentHeader comment={comment} toneUsername={toneUsername} isApiSelect={isApiSelect} />

          {/* Comment Content */}
          {isEditing ? (
            <CommentForm
              toneId={toneId}
              userDetails={userDetails!}
              parentId={comment.parent_id}
              editingComment={comment}
              onSubmit={handleEditSubmit}
              onCancel={() => setIsEditing(false)}
            />
          ) : !comment.is_deleted ? (
            <CommentContent content={comment.content!} isApiSelect={isApiSelect} />
          ) : null}

          {/* Comment Actions */}
          {!comment.is_deleted && !isEditing && (
            <CommentActions
              comment={comment}
              userDetails={userDetails}
              isOwner={isOwner}
              canReply={canReply}
              onReply={() => {
                // Trigger nested reply form in parent component
                onNestedReply?.({ id: comment.id!, username: comment.username! });
              }}
              onEdit={() => setIsEditing(true)}
              onDelete={() => handleOnReload()}
            />
          )}
        </div>
      </div>
    </div>
  );
}
